import { clsx } from 'clsx'
import { type FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { type PimProductV11 } from '@redteclab/api/clients/bully'

import { priceGetFormattedPrice } from '../../../../price'
import { productAccessorGetDataNoSnippetAttributeForInsurancePrescription } from '../../../model/productAccessor/productAccessorGetDataNoSnippetAttributeForInsurancePrescription'
import { useProductPageContext } from '../../productPageContext'
import { ProductPriceOriginal } from '../../ProductPrice/ProductPriceOriginal'
import { type ProductPrices, useProductPrices } from '../hooks/useProductPrices'

import { ProductPageVariantBonus } from './ProductPageVariantBonus'

type ProductPageVariantDetailsProps = {
  candidateVariant: PimProductV11
}

const NoLongerVariant = (
  <div className="pl-3.5 text-end text-s font-bold text-dark-primary-max">
    <FormattedMessage id="productVariants.label.noLongerVariant" />
  </div>
)

const OutOfStock = (
  <div className="text-m text-dark-primary-strong">
    <FormattedMessage id="general.label.outOfStock" />
  </div>
)

export const ProductPageVariantDetails: FC<ProductPageVariantDetailsProps> = ({
  candidateVariant,
}) => {
  const intl = useIntl()
  const { currentPrescriptionType, offerId } = useProductPageContext()

  const {
    displayPrice,
    isProductNotInCatalog,
    isProductOutOfSale,
    isProductOutOfStock,
    shouldUseReferencePrice,
  }: ProductPrices = useProductPrices({
    currentPrescriptionType,
    offerId,
    product: candidateVariant,
  })

  if (isProductNotInCatalog || isProductOutOfSale) {
    return NoLongerVariant
  }

  if (isProductOutOfStock) {
    return OutOfStock
  }

  return (
    <div
      className={clsx(
        'flex flex-1 basis-1/4 flex-col items-end justify-center px-2 desktop:basis-6/12 desktop:justify-between',
        shouldUseReferencePrice ? 'gap-0.5' : 'gap-2',
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className="text-xl font-bold text-dark-brand desktop:ml-3.5"
          data-qa-id="product-page-variant-details__display-price"
          {...productAccessorGetDataNoSnippetAttributeForInsurancePrescription(
            currentPrescriptionType,
          )}
        >
          {priceGetFormattedPrice(displayPrice?.price ?? 0, intl)}
        </div>
        <ProductPageVariantBonus product={candidateVariant} />
      </div>
      <ProductPriceOriginal
        className="text-xs text-dark-primary-strong"
        currentPrescriptionType={currentPrescriptionType}
        offerId={offerId}
        product={candidateVariant}
      />
    </div>
  )
}
